import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { noop } from "lodash-es";
import { css } from "@stubhub/broadway-foundation/css";
import { RemoveFeature, useFeatureTernary, useSimpleFeatureTernary } from "@viagogo/feature";
import type { CarouselResultsModel, CategorySummary } from "src/http/types";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { EMPTY_OBJ } from "src/shared/constants/Empty";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsUKUser } from "src/shared/utils/appContextHelper";
import { retryFetchWithOneActiveRequest } from "src/shared/utils/http/fetch-sender";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import {
  exponentialBackoffRetryDelay,
  FIVE_RETRIES,
  STALE_TIME_ONE_HOUR,
} from "src/shared/utils/query/retry-constants";
import { useWriteUrl } from "src/shared/utils/url-builder";
import type { Location } from "src/types/shared/cookies";
import { AlternativePerformers } from "src/website-apps/responsive/stubhub-performer/components/AlternativePerformers";
import { TrendingEventsCarousel } from "src/website-apps/responsive/stubhub-performer/components/performer-carousels/carousels/TrendingEventsCarousel";
import { getShouldTrackRecentlyViewedCategories } from "src/website-apps/responsive/stubhub-performer/components/performer-carousels/recentlyViewedCarouselTracking";
import { SignInBanner } from "src/website-apps/responsive/stubhub-performer/components/performer-sign-in-banner/SignInBanner";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { CarouselId } from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { GenericCategoryCarousel } from "src/website-apps/shared/components/GenericCategoryCarousel";
import { GenericEventCarousel } from "src/website-apps/shared/components/GenericEventCarousel";
import {
  ALTERNATIVE_PERFORMERS_CAROUSEL_ITEMS_KEY,
  ALTERNATIVE_PERFORMERS_CAROUSEL_KEY,
  GET_CAROUSEL_ITEMS_METHOD_NAME,
  MAYBE_INTERESTED_IN_CAROUSEL_KEY,
  PICK_UP_WHERE_YOU_LEFT_OFF_CAROUSEL_KEY,
  RECENTLY_VIEWED_CAROUSEL_KEY,
  TRENDING_EVENTS_CAROUSEL_KEY,
} from "src/website-apps/shared/constants/carousel-keys";
import { TOP_LEVEL_CATEGORY } from "src/website-apps/shared/constants/TopLevelCategory";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";

const carouselContainerStyles = css({ marginBlockStart: "primitives.300" });

interface Props {
  categorySummary: CategorySummary | undefined;
  profileUrl?: Maybe<string>;
  onLoad?: () => void;
  /** When provided (residency variant), overrides the user cookie location for the trending events carousel. */
  venueLocation?: Pick<Location, "lt" | "lg" | "n"> | undefined;
  isResidency?: boolean;
}

/**
 * Carousels to show below event grids on performer page.
 */
export const PerformerCarouselsV2: FC<Props> = memo(
  ({ categorySummary, profileUrl, onLoad = noop, venueLocation, isResidency = false }) => {
    const appContext = useAppContext();
    const model = usePerformerPageModelData();
    const storePageEvent = useStorePageEvent();
    const [state] = useEventsGridContext();
    const { dates, location } = state.filters;
    const [isLoaded, setIsLoaded] = useState(false);
    const maybeInterestedInCarouselTitle = useFormattedContent("cid$#3226572#", [categorySummary?.categoryName ?? ""]);
    const recentlyViewedCarouselTitle = useContent("cid$#3528512#");

    const isSportsTlc = categorySummary?.topLevelCategoryId === TOP_LEVEL_CATEGORY.SPORT;

    const isUKUser = useIsUKUser();

    const canParticipateInAltPerformerFeatures = useCallback(
      () => categorySummary?.topLevelCategoryId === TOP_LEVEL_CATEGORY.CONCERT,
      [categorySummary],
    );

    const rankedAltCarouselTern = useFeatureTernary(
      FeatureSetEnum.MPerformerRankedAltCarousel,
      canParticipateInAltPerformerFeatures,
    );
    const removeMaybeInterestedInCarousel = useMemo(
      () =>
        rankedAltCarouselTern({
          control: () => false,
          variant: (_, variant) => {
            switch (variant) {
              case 1: {
                return false;
              }
              case 2: {
                return true;
              }
              default: {
                return false;
              }
            }
          },
        }),
      [rankedAltCarouselTern],
    );

    const effectiveLt = venueLocation?.lt ?? location?.lt;
    const effectiveLg = venueLocation?.lg ?? location?.lg;

    const endpointUrl = useWriteUrl("", [
      ["method", GET_CAROUSEL_ITEMS_METHOD_NAME],
      ["topLevelCategoryId", appContext.topLevelCategoryId ?? undefined],
      ["categoryId", appContext.categoryId ?? undefined],
      [RECENTLY_VIEWED_CAROUSEL_KEY, true],
      [ALTERNATIVE_PERFORMERS_CAROUSEL_KEY, true],
      [MAYBE_INTERESTED_IN_CAROUSEL_KEY, !removeMaybeInterestedInCarousel],
      [TRENDING_EVENTS_CAROUSEL_KEY, true],
      ["max", 10],
      ["lt", effectiveLt],
      ["lg", effectiveLg],
    ]);

    const {
      data: carouselData = EMPTY_OBJ,
      error,
      isLoading: isPending,
    } = useQuery({
      queryKey: createQueryKey(QueryKeyPrefix.PERFORMER_CAROUSELS, {
        categoryId: appContext.categoryId,
        lt: effectiveLt ?? 0,
        lg: effectiveLg ?? 0,
      }),
      queryFn: async () => {
        return retryFetchWithOneActiveRequest<CarouselResultsModel>(
          endpointUrl,
          {
            method: "post",
          },
          {
            retries: FIVE_RETRIES,
            waitTime: exponentialBackoffRetryDelay,
            onSuccess: (index: number) => {
              storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_CAROUSEL_FETCH_RETRY_REQUEST, `SUCCESS-${index + 1}`);
            },
            onError: (index: number) => {
              storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_CAROUSEL_FETCH_RETRY_REQUEST, `ERROR-${index + 1}`);
            },
          },
        );
      },
      staleTime: STALE_TIME_ONE_HOUR,
      retry: false, // retryFetchWithOneActiveRequest already handles retries
    });

    // Data-dependent gate: only enroll users whose trending carousel would actually render —
    // non-UK users with trending items present. The batch request always asks for the trending
    // carousel (the API call cost is immaterial), so data presence is variant-independent and the
    // gate stays pristine: users who would see no difference are never enrolled.
    const canParticipateInRemoveTrendingEvents = useCallback(
      () => !isUKUser && !!carouselData[TRENDING_EVENTS_CAROUSEL_KEY]?.items?.length,
      [isUKUser, carouselData],
    );
    useEffect(() => {
      if (!(!isLoaded && !isPending && !error && Object.keys(carouselData).length > 0)) {
        return;
      }

      setIsLoaded(true);
      onLoad();
    }, [carouselData, isPending, error, isLoaded, onLoad]);

    const alternativePerformersGridItems = carouselData[ALTERNATIVE_PERFORMERS_CAROUSEL_ITEMS_KEY];
    const recentlyViewedCarouselItems = carouselData[RECENTLY_VIEWED_CAROUSEL_KEY]?.output;
    const shouldTrackRecentlyViewedCategories = getShouldTrackRecentlyViewedCategories(recentlyViewedCarouselItems);

    const excludeCategoryId = isResidency ? (appContext.categoryId ?? undefined) : undefined;
    const trendingEventsInitialData = useMemo(() => {
      const raw = carouselData[TRENDING_EVENTS_CAROUSEL_KEY];
      if (!excludeCategoryId || !raw?.items) return raw;
      return { ...raw, items: raw.items.filter((item) => item.categoryId !== excludeCategoryId) };
    }, [carouselData, excludeCategoryId]);

    const pickUpWhereYouLeftOffCarouselTitle = useContent("cid$#3575637#"); // "Event updates just for you"
    const otherShowsInLabel = useFormattedContent("cid$#4247225#", [venueLocation?.n ?? ""]); // "Other shows in {venueLocation}"
    const hasPickUpWhereYouLeftOffItems = !!carouselData[PICK_UP_WHERE_YOU_LEFT_OFF_CAROUSEL_KEY]?.items?.length;

    const hasNoUnfilteredEvents = model.totalUnfilteredEventsCount === 0;
    const canParticipateInEmptyState = useCallback(
      () => categorySummary?.topLevelCategoryId === TOP_LEVEL_CATEGORY.CONCERT && hasNoUnfilteredEvents,
      [categorySummary, hasNoUnfilteredEvents],
    );
    const isEmptyStateVariant = useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerConcertEmptyState,
      false,
      true,
      canParticipateInEmptyState,
    );

    const pickUpWhereYouLeftOffCarousel = hasPickUpWhereYouLeftOffItems ? (
      <div className={carouselContainerStyles}>
        <GenericEventCarousel
          carouselData={carouselData[PICK_UP_WHERE_YOU_LEFT_OFF_CAROUSEL_KEY]}
          isLoading={isPending}
          carouselKey={PICK_UP_WHERE_YOU_LEFT_OFF_CAROUSEL_KEY}
          title={pickUpWhereYouLeftOffCarouselTitle}
          analyticsCarouselId={CarouselId.PickUpWhereYouLeftOff}
          analyticsCategoryId={categorySummary?.categoryId}
        />
      </div>
    ) : null;

    return (
      !isPending && (
        <>
          {isEmptyStateVariant && (
            <RemoveFeature
              id={FeatureSetEnum.TMPerformerRemoveTrendingEventsCarousel}
              canParticipate={canParticipateInRemoveTrendingEvents}
              feature={() => (
                <RemoveFeature
                  id={FeatureSetEnum.TMGlobalUKRemovePopularArtistsTrendingCity}
                  canParticipate={() => isUKUser && !!carouselData[TRENDING_EVENTS_CAROUSEL_KEY]}
                  feature={() => (
                    <div className={carouselContainerStyles}>
                      <TrendingEventsCarousel
                        analyticsCategoryId={categorySummary?.categoryId}
                        categoryId={appContext.topLevelCategoryId ?? -1}
                        dates={isResidency ? null : dates}
                        excludeCategoryId={excludeCategoryId}
                        initialData={trendingEventsInitialData}
                        isLoading={isPending}
                        overrideLocation={venueLocation}
                        title={venueLocation ? otherShowsInLabel : undefined}
                        userLocation={location}
                        useClickableLocation
                      />
                    </div>
                  )}
                />
              )}
            />
          )}
          {!isResidency && !isSportsTlc && !isEmptyStateVariant && (
            <RemoveFeature
              id={FeatureSetEnum.TMPerformerRemovePopularArtistsCarousel}
              canParticipate={() => !isUKUser && !!alternativePerformersGridItems?.length}
              feature={() => (
                <RemoveFeature
                  id={FeatureSetEnum.TMGlobalUKRemovePopularArtistsTrendingCity}
                  canParticipate={() => isUKUser && !!alternativePerformersGridItems?.length}
                  feature={() => (
                    <AlternativePerformers
                      altPerformerItems={alternativePerformersGridItems?.slice(0, 6) ?? []}
                      isLoading={isPending}
                      categoryId={categorySummary?.categoryId}
                    />
                  )}
                />
              )}
            />
          )}
          {!isResidency && pickUpWhereYouLeftOffCarousel}
          {!isResidency && (
            <div className={carouselContainerStyles}>
              <GenericCategoryCarousel
                carouselItems={carouselData[MAYBE_INTERESTED_IN_CAROUSEL_KEY]}
                isLoading={isPending}
                carouselKey={MAYBE_INTERESTED_IN_CAROUSEL_KEY}
                title={maybeInterestedInCarouselTitle}
                analyticsCarouselId={CarouselId.MaybeInterestedIn}
                analyticsCategoryId={categorySummary?.categoryId}
              />
            </div>
          )}
          {!isResidency && !appContext.isUserLoggedIn && <SignInBanner profileUrl={profileUrl ?? ""} />}
          {!isEmptyStateVariant && (
            <RemoveFeature
              id={FeatureSetEnum.TMPerformerRemoveTrendingEventsCarousel}
              canParticipate={canParticipateInRemoveTrendingEvents}
              feature={() => (
                <RemoveFeature
                  id={FeatureSetEnum.TMGlobalUKRemovePopularArtistsTrendingCity}
                  canParticipate={() => isUKUser && !!carouselData[TRENDING_EVENTS_CAROUSEL_KEY]}
                  feature={() => (
                    <div className={carouselContainerStyles}>
                      <TrendingEventsCarousel
                        analyticsCategoryId={categorySummary?.categoryId}
                        categoryId={appContext.topLevelCategoryId ?? -1}
                        dates={isResidency ? null : dates}
                        excludeCategoryId={excludeCategoryId}
                        initialData={trendingEventsInitialData}
                        isLoading={isPending}
                        overrideLocation={venueLocation}
                        title={venueLocation ? otherShowsInLabel : undefined}
                        userLocation={location}
                      />
                    </div>
                  )}
                />
              )}
            />
          )}
          {!carouselData[PICK_UP_WHERE_YOU_LEFT_OFF_CAROUSEL_KEY]?.items?.length &&
            !!carouselData[RECENTLY_VIEWED_CAROUSEL_KEY]?.output?.length && (
              <div className={carouselContainerStyles}>
                <GenericCategoryCarousel
                  carouselItems={carouselData[RECENTLY_VIEWED_CAROUSEL_KEY].output}
                  isLoading={isPending}
                  carouselKey={RECENTLY_VIEWED_CAROUSEL_KEY}
                  title={recentlyViewedCarouselTitle}
                  analyticsCarouselId={shouldTrackRecentlyViewedCategories ? CarouselId.RecentlyViewed : undefined}
                  analyticsCategoryId={categorySummary?.categoryId}
                />
              </div>
            )}
        </>
      )
    );
  },
);

PerformerCarouselsV2.displayName = "PerformerCarouselsV2";
